/**
 * FederationPeerEditSlideout — Stage 3d-frontend.
 *
 * Create or edit a federation peer in the caller's org. Dual mode:
 *   - peer=null   → create form (POST /api/v1/identity/federation-peers)
 *   - peer={...}  → edit form prefilled from the row (PUT /federation-peers/{id})
 *
 * Used by IdentityFederationPeersPageV2.
 */
import React, { useEffect, useState } from 'react';
import { Save, X as XIcon, AlertTriangle } from 'lucide-react';
import { Slideout } from '../Slideout';
import { Button } from '../Button';
import { Pill } from '../Pill';
import {
  createIdentityFederationPeer,
  updateIdentityFederationPeer,
} from '../../../services/api';

export function FederationPeerEditSlideout({ isOpen, onClose, onSaved, peer }) {
  const isEdit = !!peer?.id;

  const [name, setName] = useState('');
  const [spiffeId, setSpiffeId] = useState('');
  const [direction, setDirection] = useState('BIDIRECTIONAL');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Sync initial state when the slideout opens for a specific peer.
  useEffect(() => {
    if (!isOpen) return;
    if (peer) {
      setName(peer.name || '');
      setSpiffeId(peer.spiffeId || '');
      setDirection(peer.direction || 'BIDIRECTIONAL');
      setIsActive(peer.isActive !== false);
    } else {
      setName(''); setSpiffeId(''); setDirection('BIDIRECTIONAL'); setIsActive(true);
    }
    setError(null);
  }, [isOpen, peer]);

  const inputCls =
    'v2-focus-ring w-full px-3 py-2 rounded-v2-sm border border-v2-border bg-v2-surface text-v2-ink text-[13px]';

  const handleSave = async () => {
    setError(null);
    if (!name.trim()) { setError('Name is required.'); return; }
    if (!spiffeId.trim() || !spiffeId.startsWith('spiffe://')) {
      setError('SPIFFE ID must start with spiffe://');
      return;
    }
    const body = {
      name: name.trim(),
      spiffeId: spiffeId.trim(),
      direction,
      isActive,
    };
    setSaving(true);
    const res = isEdit
      ? await updateIdentityFederationPeer(peer.id, body)
      : await createIdentityFederationPeer(body);
    setSaving(false);
    if (res?.ok === false) {
      setError(res.error || 'Save failed.');
      return;
    }
    if (typeof onSaved === 'function') onSaved(res?.data);
    onClose();
  };

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={500}
      ariaLabel={isEdit ? 'Edit federation peer' : 'Create federation peer'}
      header={
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold text-v2-ink m-0">
            {isEdit ? 'Edit federation peer' : 'New federation peer'}
          </h2>
          <Pill tone="trust" size="sm">Identity</Pill>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-v2-border-subtle">
          <Button variant="ghost" size="sm" iconLeft={<XIcon size={13} />} onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" iconLeft={<Save size={13} />}
                  onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create peer')}
          </Button>
        </div>
      }
    >
      <div className="px-4 py-4 flex flex-col gap-4">
        {error && (
          <div className="bg-v2-danger-soft border border-v2-danger/30 rounded-v2-md px-3 py-2 text-[12.5px] text-v2-danger flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <div>
          <label className="block text-[11px] text-v2-ink-secondary mb-1">Display name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="FinNet Clearing" className={inputCls} />
        </div>
        <div>
          <label className="block text-[11px] text-v2-ink-secondary mb-1">Peer SPIFFE ID</label>
          <input type="text" value={spiffeId} onChange={(e) => setSpiffeId(e.target.value)}
                 placeholder="spiffe://finnet.clearing.io" className={inputCls} />
        </div>
        <div>
          <label className="block text-[11px] text-v2-ink-secondary mb-1">Direction</label>
          <select value={direction} onChange={(e) => setDirection(e.target.value)} className={inputCls}>
            <option value="BIDIRECTIONAL">Bidirectional (we trust them; they trust us)</option>
            <option value="INBOUND_ONLY">Inbound only (we trust them; they don't trust us)</option>
            <option value="OUTBOUND_ONLY">Outbound only (they trust us; we don't trust them)</option>
          </select>
        </div>
        <div className="flex items-center gap-2.5">
          <input type="checkbox" id="peerActive"
                 checked={isActive}
                 onChange={(e) => setIsActive(e.target.checked)} />
          <label htmlFor="peerActive" className="text-[13px] text-v2-ink">
            Active (uncheck to soft-retire — preserves audit history)
          </label>
        </div>
      </div>
    </Slideout>
  );
}

export default FederationPeerEditSlideout;
